import {
  PresentationContent,
  PresentationModification,
  ToolCall,
  ToolResult,
  ChatMessage,
  ReferenceSlideImage,
} from "../../../types";
import { ModelConfig } from "../../../modelConfig";
import { buildApiUrl } from "../../../config";
import {
  AiRequestOptions,
  StreamCallbacks,
  ToolExecutionPolicy,
  AgentToolContext,
  ReferenceDeckExamples,
} from "../types";
import {
  readModelError,
  prepareRequestData,
  buildToolFollowUpTextInput,
  fetchWithRetry,
  tryRepairTruncatedJson,
  stripMarkdownFences,
} from "../../../utils";
import { withCredentials } from "../../apiClient";
import { withPptxLangfuseMetadata, type PptxLangfusePhase } from "../../../langfuseTrace";
import {
  executeTools,
  mergeReferenceImages,
  formatToolCallArgs,
  isToolAllowed,
  buildBlockedToolResult,
} from "../tools";
import { buildOpenAILegacyToolFollowUpPrompt, getSystemPrompt } from "../promptCatalog";
import { finalizeModelModifications } from "../modificationFinalizer";
import { sanitizeCompanyLogoToolData, sanitizeToolResultsForModelOutput } from "../logoCompanyRefs";
import {
  buildOpenAIModificationResponseFormat,
  buildOpenAINativeTools,
  OpenAIFunctionToolDefinition,
} from "../openaiSchemas";
import {
  getOpenAINativeRolloutTelemetry,
  isOpenAINativeEnabled,
  recordOpenAINativeMetric,
} from "../openaiNativeRollout";
import { uploadGoldenArtifactJson } from "../../goldenArtifactsService";
import { runGoldenCaptureStep } from "../../../utils/goldenCapture";
import { parseDeckPlanFromToolArgs, parseSlideOutlinePatch } from "../deckPlanAgentTools";
import {
  extractMessageTextContent,
  extractStructuredPayload,
  parseJsonText,
} from "../openaiResponseParsing";
import { buildOpenAIImageUrl } from "../../../utils/imagePayload";
import { mapOpenAIStreamEvent } from "../agentStream";
import type { AgentEvent } from "../agentStream";

// Attach per-run Langfuse trace metadata to a provider request, but only when a
// run context is present so out-of-scope PowerPoint calls stay untraced. Mirrors
// the Excel provider's per-request metadata so PPT traces carry the same
// provider/model/tool attribution for cost and debugging.
function withRequestLangfuse(
  init: RequestInit,
  options: AiRequestOptions,
  phase: PptxLangfusePhase,
  extra: Record<string, unknown> = {}
): RequestInit {
  if (!options.langfuseContext) return init;
  return withPptxLangfuseMetadata(init, options.langfuseContext, {
    phase,
    provider: "openai",
    ...extra,
  });
}

// Derive per-request Langfuse metadata (model/reasoning/stream/toolCount) from an
// outgoing Responses API body so streamed/native calls match the Excel provider's
// trace attribution.
function buildOpenAIRequestLangfuseExtra(
  requestBody: Record<string, unknown>,
  label?: string
): Record<string, unknown> {
  const reasoning = requestBody.reasoning as { effort?: unknown } | undefined;
  return {
    ...(typeof requestBody.model === "string" ? { model: requestBody.model } : {}),
    ...(label ? { label } : {}),
    ...(reasoning && typeof reasoning.effort === "string"
      ? { reasoningEffort: reasoning.effort }
      : {}),
    stream: requestBody.stream === true,
    ...(Array.isArray(requestBody.tools) ? { toolCount: requestBody.tools.length } : {}),
  };
}

function resolveSeedPhase(options: AiRequestOptions): PptxLangfusePhase {
  return options.langfusePhase ?? "chat";
}

async function queryOpenAILegacy(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const { textMessage } = prepareRequestData(presentationContent, userQuery, chatHistory);
  const systemPrompt = getSystemPrompt(toolPolicy);

  let input: unknown;
  if (slideImages && slideImages.length > 0) {
    const totalKB = slideImages.reduce((sum, img) => sum + img.length / 1024, 0);
    console.log(
      `[OPENAI] Including ${slideImages.length} slide image(s) (${totalKB.toFixed(0)}KB total) for visual context`
    );

    const imageUrls = slideImages
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl));
    const contentParts: unknown[] = imageUrls.map((imageUrl) => ({
      type: "input_image",
      image_url: imageUrl,
      detail: imageUrls.length > 2 ? "low" : "auto",
    }));
    contentParts.push({ type: "input_text", text: textMessage });
    input = [{ role: "user", content: contentParts }];
  } else {
    input = textMessage;
  }
  input = applyPreludeUserMessages(input, options.preludeUserMessages);

  const requestPayload: Record<string, unknown> = {
    model: model.model,
    instructions: systemPrompt,
    input,
    // `summary: "auto"` is REQUIRED to get reasoning summary text back
    // from the Responses API — without it the "Thought process"
    // accordion stays empty even when the model reasoned.
    reasoning: { effort: "low", summary: "auto" },
    text: { format: { type: "json_object" } },
    store: false,
    stream: false,
    service_tier: "priority",
  };
  const requestBody = JSON.stringify(requestPayload);

  const requestSizeKB = (requestBody.length / 1024).toFixed(1);
  console.log(
    `[OPENAI] Sending request: model=${model.model}, size=${requestSizeKB}KB, images=${slideImages?.length || 0}`
  );
  const t0 = Date.now();

  try {
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/openai/responses"),
      withRequestLangfuse(
        withCredentials({
          method: "POST",
          body: requestBody,
          signal: options.signal,
        }),
        options,
        resolveSeedPhase(options),
        { model: model.model, label: "OPENAI", reasoningEffort: "low", stream: false }
      ),
      3,
      "OPENAI"
    );

    if (!response.ok) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      const modelError = await readModelError(response, "OpenAI request failed");
      callbacks.onError(`API error (${elapsed}s): ${modelError}`);
      return;
    }

    const data = await response.json();
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    console.log(
      `[OPENAI] Response received in ${elapsed}s, status=${data.status}:`,
      JSON.stringify(data).slice(0, 500)
    );

    if (data.status === "incomplete") {
      const reason = data.incomplete_details?.reason || "unknown";
      console.warn(`[OPENAI] Response was TRUNCATED (reason: ${reason})`);
      if (reason === "max_output_tokens") {
        console.warn(
          `[OPENAI] max_output_tokens limit hit — response was cut off. Will attempt to parse partial JSON.`
        );
      }
    }

    let fullContent = "";
    let reasoningContent = "";

    if (data.output && Array.isArray(data.output)) {
      for (const item of data.output) {
        if (item.type === "reasoning" && item.summary) {
          for (const s of item.summary) {
            if (s.type === "summary_text" && s.text) {
              reasoningContent += s.text;
            }
          }
        }
        if (item.type === "message" && item.content) {
          for (const c of item.content) {
            if (c.type === "output_text" && c.text) {
              fullContent = c.text;
            }
          }
        }
      }
    }

    console.log(
      `[OPENAI] Reasoning: ${reasoningContent.length} chars, Content: ${fullContent.length} chars`
    );

    if (reasoningContent) {
      callbacks.onThinking(reasoningContent);
    }

    let parsedResponse: Record<string, unknown> | null = null;
    try {
      parsedResponse = JSON.parse(fullContent);
    } catch {
      if (fullContent.includes('"slides"') && data.status === "incomplete") {
        console.log("[OPENAI] Attempting to repair truncated JSON...");
        const repaired = tryRepairTruncatedJson(fullContent);
        if (repaired) {
          try {
            parsedResponse = JSON.parse(repaired);
            fullContent = repaired;
            console.log(`[OPENAI] Successfully repaired truncated JSON (${repaired.length} chars)`);
          } catch {
            console.warn("[OPENAI] Repair attempt failed");
          }
        }
      }
    }

    if (parsedResponse?.tools && Array.isArray(parsedResponse.tools)) {
      const toolCalls: ToolCall[] = parsedResponse.tools;
      const blockedToolResults = toolCalls
        .filter((call) => !isToolAllowed(call.tool, toolPolicy))
        .map((call) =>
          buildBlockedToolResult(
            call.tool,
            `Tool ${call.tool} is disabled for this request context.`
          )
        );
      const allowedToolCalls = toolCalls.filter((call) => isToolAllowed(call.tool, toolPolicy));

      for (const call of allowedToolCalls) {
        callbacks.onToolCall(call.tool, formatToolCallArgs(call));
      }
      for (const blocked of blockedToolResults) {
        console.log(`[TOOL] Skipping disabled tool call: ${blocked.tool}`);
      }

      try {
        console.log(`[TOOL] Executing ${allowedToolCalls.length} allowed tool call(s)...`);
        const executedTools =
          allowedToolCalls.length > 0
            ? await executeTools(allowedToolCalls, {
                presentationContent,
                finsterDataSources: toolPolicy?.finsterDataSources,
                toolPolicy,
                agentToolContext: options.agentToolContext,
                langfuseContext: options.langfuseContext,
              })
            : { results: [], referenceImages: [] as ReferenceSlideImage[] };
        const toolResults = [...blockedToolResults, ...executedTools.results];
        if (toolResults.length > 0) {
          console.log("[TOOL] Results:", JSON.stringify(toolResults).slice(0, 500));
        }
        if (executedTools.referenceImages.length > 0) {
          console.log(
            `[TOOL] Captured ${executedTools.referenceImages.length} slide reference image(s) from get_slide_images`
          );
        }

        if (callbacks.onToolResult) {
          callbacks.onToolResult(toolResults);
        }

        const followUpImages = mergeReferenceImages(
          slideImages,
          flattenReferenceImages(executedTools.referenceImages)
        );
        if (followUpImages && followUpImages.length > 0) {
          console.log(
            `[TOOL] Using ${followUpImages.length} total reference image(s) for follow-up`
          );
        }

        console.log("[TOOL] Making follow-up call with tool results...");
        const followUpContent = await callWithToolResultsLegacy(
          presentationContent,
          userQuery,
          fullContent,
          toolResults,
          model,
          callbacks.onThinking,
          followUpImages,
          toolPolicy,
          options
        );

        if (followUpContent) {
          console.log(`[TOOL] Follow-up returned ${followUpContent.length} chars`);
          fullContent = followUpContent;
        } else {
          console.error("[TOOL] Follow-up returned null - no content from AI after tool results");
        }
      } catch (toolError) {
        console.error("[TOOL] Error during tool execution:", toolError);
        callbacks.onError(
          `Tool execution failed: ${toolError instanceof Error ? toolError.message : String(toolError)}`
        );
        return;
      }
    }

    if (fullContent.trim()) {
      try {
        const jsonStr = stripMarkdownFences(fullContent);

        let modifications: PresentationModification;
        try {
          modifications = JSON.parse(jsonStr);
        } catch (initialParseErr) {
          console.warn(
            `[OPENAI] Initial parse failed, attempting repair... (${initialParseErr})`
          );
          const repaired = tryRepairTruncatedJson(jsonStr);
          if (repaired) {
            modifications = JSON.parse(repaired);
            console.log(`[OPENAI] Repaired truncated JSON successfully (${repaired.length} chars)`);
          } else {
            throw initialParseErr;
          }
        }

        callbacks.onComplete(finalizeModelModifications(modifications));
      } catch (parseError) {
        console.error("Failed to parse JSON. Content length:", fullContent.length);
        console.error("FULL CONTENT:", fullContent);
        console.error("Parse error:", parseError);

        const truncationNote =
          data.status === "incomplete"
            ? " (TRUNCATED — AI response was cut off, try a simpler request)"
            : "";
        const preview =
          fullContent.length > 300
            ? fullContent.slice(0, 150) + "..." + fullContent.slice(-150)
            : fullContent;
        callbacks.onError(
          `JSON parse failed (${fullContent.length} chars)${truncationNote}: ${parseError instanceof Error ? parseError.message : "Invalid JSON"}\n\nContent: ${preview}`
        );
      }
    } else {
      console.error(
        "[OPENAI DEBUG] Empty fullContent! Reasoning was:",
        reasoningContent.slice(0, 200)
      );
      callbacks.onError(
        `Empty response from AI. Check browser console for debug info. Reasoning captured: ${reasoningContent ? "yes" : "no"}`
      );
    }
  } catch (error) {
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    const errMsg = error instanceof Error ? error.message : "Unknown error";
    console.error(`[OPENAI] Request failed after ${elapsed}s:`, error);
    callbacks.onError(
      `Request failed after ${elapsed}s: ${errMsg}${errMsg === "Load failed" ? " (WebKit timeout — the AI took too long to respond)" : ""}`
    );
  }
}

async function callWithToolResultsLegacy(
  presentationContent: PresentationContent,
  userQuery: string,
  previousResponse: string,
  toolResults: ToolResult[],
  model: ModelConfig,
  onThinking?: (text: string) => void,
  slideImages?: string[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<string | null> {
  const systemPrompt = getSystemPrompt(toolPolicy);
  const cleanResults = sanitizeToolResultsForModelOutput(toolResults);

  const followUpText = buildOpenAILegacyToolFollowUpPrompt({
    previousResponse,
    serializedToolResults: JSON.stringify(cleanResults, null, 2),
    toolPolicy,
  });
  const textInput = buildToolFollowUpTextInput(
    presentationContent,
    userQuery,
    followUpText,
    "TOOL-FOLLOWUP"
  );

  let input: unknown;
  if (slideImages && slideImages.length > 0) {
    console.log(`[TOOL-FOLLOWUP] Including ${slideImages.length} reference image(s)`);
    const contentParts: unknown[] = slideImages
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
      .map((imageUrl) => ({
        type: "input_image",
        image_url: imageUrl,
        detail: slideImages.length > 2 ? "low" : "auto",
      }));
    contentParts.push({ type: "input_text", text: textInput });
    input = [{ role: "user", content: contentParts }];
  } else {
    input = textInput;
  }
  input = applyPreludeUserMessages(input, options.preludeUserMessages);

  const t0 = Date.now();
  try {
    const requestPayload: Record<string, unknown> = {
      model: model.model,
      instructions: systemPrompt,
      input,
      // See note on the streaming variant — `summary: "auto"` is
      // required for the Responses API to return reasoning text.
      reasoning: { effort: "low", summary: "auto" },
      text: { format: { type: "json_object" } },
      store: false,
      service_tier: "priority",
    };
    const requestBody = JSON.stringify(requestPayload);
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/openai/responses"),
      withRequestLangfuse(
        withCredentials({
          method: "POST",
          signal: options.signal,
          body: requestBody,
        }),
        options,
        "multi_hop_executor",
        { model: model.model, label: "TOOL-FOLLOWUP", reasoningEffort: "low", stream: false }
      ),
      3,
      "TOOL-FOLLOWUP"
    );

    if (!response.ok) {
      console.error(
        `[TOOL-FOLLOWUP] Failed (${response.status}) after ${((Date.now() - t0) / 1000).toFixed(1)}s`
      );
      return null;
    }

    const data = await response.json();
    console.log(`[TOOL-FOLLOWUP] Response in ${((Date.now() - t0) / 1000).toFixed(1)}s`);
    const output = data.output || [];
    let contentText = "";
    let reasoningText = "";

    for (const item of output) {
      if (item.type === "reasoning" && item.summary) {
        for (const s of item.summary) {
          if (s.type === "summary_text" && s.text) {
            reasoningText += s.text;
          }
        }
      }
      if (item.type === "message" && item.content) {
        for (const c of item.content) {
          if (c.type === "output_text" && c.text) {
            contentText = c.text;
          }
        }
      }
    }

    if (reasoningText && onThinking) {
      onThinking(reasoningText);
    }

    return contentText || null;
  } catch (error) {
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    console.error(`[TOOL-FOLLOWUP] Error after ${elapsed}s:`, error);
    return null;
  }
}

// Tool-loop depth per turn. The unified agentic agent needs many rounds
// (plan -> apply_edits -> observe -> fix ...). A hard ceiling plus a per-turn
// cumulative tool-call budget bound cost regardless of configuration, and the
// forced-finalization safety net still runs at the boundary.
const OPENAI_NATIVE_TOOL_LOOP_HARD_CEILING = 50;
const OPENAI_NATIVE_TOOL_LOOP_DEFAULT_AGENTIC = 24;
const OPENAI_NATIVE_TOOL_LOOP_LIMIT_KEY = "finster_openai_native_tool_loop_limit";
const OPENAI_NATIVE_TOOL_CALL_BUDGET = 64;

function getOpenAINativeToolLoopLimit(): number {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(OPENAI_NATIVE_TOOL_LOOP_LIMIT_KEY);
      if (raw) {
        const parsed = Number.parseInt(raw, 10);
        if (Number.isFinite(parsed) && parsed >= 1) {
          return Math.min(parsed, OPENAI_NATIVE_TOOL_LOOP_HARD_CEILING);
        }
      }
    }
  } catch {
    // localStorage unavailable (non-browser context); fall through to defaults.
  }
  return OPENAI_NATIVE_TOOL_LOOP_DEFAULT_AGENTIC;
}

const FUNCTION_CALL_NO_OUTPUT_ERROR = "Function did not return any output.";
const FUNCTION_CALL_MISSING_ID_ERROR =
  "OpenAI returned tool call(s) without call_id/id; cannot continue safely.";
const OPENAI_FUNCTION_TOOL_NAMES = new Set<ToolCall["tool"]>([
  "get_company_logo",
  "finster_query",
  "factset_ai_query",
  "get_slide_images",
  "apply_edits",
  "get_slides_info",
  "insert_library_slide",
  "apply_preslink",
  "list_slide_library_collections",
  "search_slide_library",
  "get_library_slide_previews",
  "present_deck_plan",
  "update_deck_plan",
  "active_graph",
  "list_active_graph_types",
  "id_lookup",
]);

interface OpenAIResponseSummaryText {
  type?: string;
  text?: string;
}

interface OpenAIResponseContentItem {
  type?: string;
  text?: string;
  json?: unknown;
}

interface OpenAIResponseFunctionCallDescriptor {
  name?: string;
  arguments?: string | Record<string, unknown> | null;
  id?: string;
  call_id?: string;
}

interface OpenAIResponseOutputItem {
  type?: string;
  id?: string;
  name?: string;
  call_id?: string;
  tool_call_id?: string;
  arguments?: string | Record<string, unknown> | null;
  function?: OpenAIResponseFunctionCallDescriptor;
  summary?: OpenAIResponseSummaryText[];
  content?: OpenAIResponseContentItem[];
}

interface OpenAIResponsePayload {
  id?: string;
  status?: string;
  incomplete_details?: { reason?: string };
  output?: OpenAIResponseOutputItem[];
  output_text?: string;
}

interface NativeFunctionCall {
  callId: string;
  name: string;
  rawArguments: string;
}

export interface NativeFunctionCallOutputItem {
  type: "function_call_output";
  call_id: string;
  output: string;
}

interface NativeToolHandlingResult {
  functionCallOutputs: NativeFunctionCallOutputItem[];
  toolResults: ToolResult[];
  referenceImages: ReferenceSlideImage[];
}

interface GoldenToolTraceEntry {
  sequence: number;
  loopIndex?: number;
  callIndexInLoop: number;
  callId: string;
  toolName: string;
  subdirectory?: string;
  status: "pending" | "success" | "error" | "blocked" | "parse_error";
  success?: boolean;
  error?: string;
  files: string[];
  requestedReturn?: { json?: boolean; image?: boolean };
  referenceImageCount?: number;
  hasSlidesInfo?: boolean;
  createdSlideCount?: number;
}

interface NativeLoopCallbacks {
  onThinking?: (text: string) => void;
  onToolCall?: (tool: string, args: string) => void;
  onToolResult?: (results: ToolResult[]) => void;
  onAgentEvent?: (event: AgentEvent) => void;
}

interface ContinueOpenAIWithFunctionOutputsOptions {
  model: ModelConfig;
  systemPrompt: string;
  toolPolicy?: ToolExecutionPolicy;
  conversationHistory: unknown[];
  functionCallOutputs: NativeFunctionCallOutputItem[];
  slideImages?: string[];
  loopLabel?: string;
  signal?: AbortSignal;
  onStreamEvent?: (event: OpenAIResponsesStreamEvent) => void;
  /** Per-run Langfuse context so continuation hops join the run trace. */
  langfuseContext?: AiRequestOptions["langfuseContext"];
  preludeUserMessages?: AiRequestOptions["preludeUserMessages"];
}

interface ContinueOpenAIWithFunctionOutputsResult {
  responseData: OpenAIResponsePayload;
  conversationHistory: unknown[];
}

interface ExtractedNativeFunctionCallsResult {
  calls: NativeFunctionCall[];
  missingCallIdCount: number;
}

interface ReplayFunctionCallState {
  unresolvedCalls: NativeFunctionCall[];
  missingCallIdCount: number;
}

function extractReasoningContent(data: OpenAIResponsePayload): string {
  let reasoningContent = "";
  const outputItems = Array.isArray(data.output) ? data.output : [];
  for (const item of outputItems) {
    if (item.type !== "reasoning" || !Array.isArray(item.summary)) {
      continue;
    }
    for (const summaryEntry of item.summary) {
      if (summaryEntry?.type === "summary_text" && typeof summaryEntry.text === "string") {
        reasoningContent += summaryEntry.text;
      }
    }
  }
  return reasoningContent;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isNativeFunctionCallType(typeValue: unknown): boolean {
  return typeValue === "function_call" || typeValue === "tool_call";
}

function extractFunctionCallIdFromItem(item: Record<string, unknown>): string | null {
  const functionPayload = asRecord(item.function);
  return (
    readNonEmptyString(item.call_id) ||
    readNonEmptyString(item.id) ||
    readNonEmptyString(item.tool_call_id) ||
    (functionPayload ? readNonEmptyString(functionPayload.call_id) : null) ||
    (functionPayload ? readNonEmptyString(functionPayload.id) : null)
  );
}

function extractFunctionNameFromItem(item: Record<string, unknown>): string {
  const functionPayload = asRecord(item.function);
  return (
    readNonEmptyString(item.name) ||
    (functionPayload ? readNonEmptyString(functionPayload.name) : null) ||
    "unknown_tool"
  );
}

function normalizeFunctionArguments(rawArguments: unknown): string {
  if (typeof rawArguments === "string") {
    return rawArguments;
  }
  if (rawArguments === undefined || rawArguments === null) {
    return "{}";
  }
  try {
    return JSON.stringify(rawArguments);
  } catch {
    return "{}";
  }
}

function extractFunctionArgumentsFromItem(item: Record<string, unknown>): string {
  if ("arguments" in item) {
    return normalizeFunctionArguments(item.arguments);
  }
  const functionPayload = asRecord(item.function);
  if (functionPayload && "arguments" in functionPayload) {
    return normalizeFunctionArguments(functionPayload.arguments);
  }
  return "{}";
}

function extractNativeFunctionCallsFromItems(items: unknown[]): ExtractedNativeFunctionCallsResult {
  const calls: NativeFunctionCall[] = [];
  let missingCallIdCount = 0;
  for (const rawItem of items) {
    const item = asRecord(rawItem);
    if (!item || !isNativeFunctionCallType(item.type)) {
      continue;
    }
    const callId = extractFunctionCallIdFromItem(item);
    if (!callId) {
      missingCallIdCount += 1;
      continue;
    }
    calls.push({
      callId,
      name: extractFunctionNameFromItem(item),
      rawArguments: extractFunctionArgumentsFromItem(item),
    });
  }
  return {
    calls,
    missingCallIdCount,
  };
}

function extractNativeFunctionCallsWithDiagnostics(
  data: OpenAIResponsePayload
): ExtractedNativeFunctionCallsResult {
  const outputItems = Array.isArray(data.output) ? data.output : [];
  return extractNativeFunctionCallsFromItems(outputItems as unknown[]);
}

function extractFunctionCallOutputIdFromItem(item: Record<string, unknown>): string | null {
  return (
    readNonEmptyString(item.call_id) ||
    readNonEmptyString(item.tool_call_id) ||
    readNonEmptyString(item.id)
  );
}

function collectReplayFunctionCallState(replayItems: unknown[]): ReplayFunctionCallState {
  const extractedCalls = extractNativeFunctionCallsFromItems(replayItems);
  const outputCallIds = new Set<string>();
  for (const rawItem of replayItems) {
    const item = asRecord(rawItem);
    if (!item || item.type !== "function_call_output") {
      continue;
    }
    const callId = extractFunctionCallOutputIdFromItem(item);
    if (callId) {
      outputCallIds.add(callId);
    }
  }

  const unresolvedCalls: NativeFunctionCall[] = [];
  const seenUnresolvedCallIds = new Set<string>();
  for (const call of extractedCalls.calls) {
    if (outputCallIds.has(call.callId) || seenUnresolvedCallIds.has(call.callId)) {
      continue;
    }
    unresolvedCalls.push(call);
    seenUnresolvedCallIds.add(call.callId);
  }

  return {
    unresolvedCalls,
    missingCallIdCount: extractedCalls.missingCallIdCount,
  };
}

function buildFallbackOutputsForUnansweredCalls(
  calls: NativeFunctionCall[],
  errorMessage: string
): { fallbackResults: ToolResult[]; fallbackOutputs: NativeFunctionCallOutputItem[] } {
  const fallbackResults: ToolResult[] = calls.map((call) => ({
    tool: call.name || "unknown_tool",
    success: false,
    error: errorMessage,
  }));
  const fallbackOutputs: NativeFunctionCallOutputItem[] = calls.map((call, index) => ({
    type: "function_call_output",
    call_id: call.callId,
    output: JSON.stringify(normalizeToolResultForOutput(fallbackResults[index])),
  }));
  return {
    fallbackResults,
    fallbackOutputs,
  };
}

function normalizeToolResultForOutput(result: ToolResult): Record<string, unknown> {
  let normalizedData = result.data;
  if (normalizedData && typeof normalizedData === "object") {
    if (result.tool === "get_company_logo") {
      normalizedData = sanitizeCompanyLogoToolData(normalizedData);
    } else if ("debugLog" in normalizedData) {
      const { debugLog: _debugLog, ...rest } = normalizedData as Record<string, unknown>;
      normalizedData = rest;
    }
  }
  const payload: Record<string, unknown> = {
    tool: result.tool,
    success: result.success,
  };
  if (normalizedData !== undefined) {
    payload.data = normalizedData;
  }
  if (result.error) {
    payload.error = result.error;
  }
  return payload;
}

function jsonSafeClone<T>(value: T): T {
  return JSON.parse(
    JSON.stringify(value, (_key, nestedValue) => {
      if (nestedValue instanceof Error) {
        return { name: nestedValue.name, message: nestedValue.message, stack: nestedValue.stack };
      }
      return nestedValue;
    })
  ) as T;
}

function buildReferenceImageArtifacts(
  images: ReferenceSlideImage[] | undefined
): Array<Record<string, unknown>> {
  return (images || []).map(({ slideId, image: base64 }, index) => ({
    index,
    slideId,
    base64,
    base64Length: base64.length,
    approxBytes: Math.ceil((base64.length * 3) / 4),
  }));
}

function flattenReferenceImages(images: ReferenceSlideImage[] | undefined): string[] | undefined {
  if (!images || images.length === 0) return undefined;
  return images.map((image) => image.image);
}

function updateLatestReferenceImagesBySlide(
  latestImagesBySlideId: Map<string, string>,
  images: ReferenceSlideImage[],
  maxImages: number
): string[] | undefined {
  for (const { slideId, image } of images) {
    if (!slideId || !image) continue;
    latestImagesBySlideId.delete(slideId);
    latestImagesBySlideId.set(slideId, image);
  }

  while (latestImagesBySlideId.size > maxImages) {
    const oldestSlideId = latestImagesBySlideId.keys().next().value;
    if (!oldestSlideId) break;
    latestImagesBySlideId.delete(oldestSlideId);
  }

  const latestImages = Array.from(latestImagesBySlideId.values());
  return latestImages.length > 0 ? latestImages : undefined;
}

function buildRequestedReturn(call: ToolCall | undefined): { json?: boolean; image?: boolean } | undefined {
  if (!call || (call.tool !== "apply_edits" && call.tool !== "get_slides_info")) return undefined;
  const requested: { json?: boolean; image?: boolean } = {};
  if (call.returnJson === true) requested.json = true;
  if (call.returnImage === true) requested.image = true;
  return Object.keys(requested).length > 0 ? requested : undefined;
}

function hasSlidesInfo(result: ToolResult | undefined): boolean | undefined {
  const data = result?.data;
  if (!data || typeof data !== "object") return undefined;
  return "slidesInfo" in data ? true : undefined;
}

function getCreatedSlideCount(result: ToolResult | undefined): number | undefined {
  const data = result?.data;
  if (!data || typeof data !== "object") return undefined;
  const createdSlides = (data as Record<string, unknown>).createdSlides;
  return Array.isArray(createdSlides) ? createdSlides.length : undefined;
}

function buildToolTraceFiles(toolName: string): string[] {
  const files = ["tool_call.json"];
  if (toolName === "apply_edits") {
    files.push("operations.json", "prepared_batch_operations.json");
  }
  files.push("tool_output.json");
  return files;
}

function resolveTraceStatus(input: {
  pending: {
    blockedReason?: string;
    parseError?: string;
  };
  toolResult?: ToolResult;
}): GoldenToolTraceEntry["status"] {
  if (input.pending.blockedReason) return "blocked";
  if (input.pending.parseError) return "parse_error";
  return input.toolResult?.success ? "success" : "error";
}

async function captureGoldenToolTrace(
  ctx: AgentToolContext | undefined,
  entries: GoldenToolTraceEntry[]
): Promise<void> {
  if (!ctx?.goldenArtifactRun) return;
  const traceSubdirectory = ctx.goldenToolTraceSubdirectory;
  await runGoldenCaptureStep(ctx.goldenArtifactRun, ctx.log ?? (() => {}), "tool_trace.json", async () => {
    await uploadGoldenArtifactJson(
      ctx.goldenArtifactRun!.runId,
      "tool_trace",
      {
        runId: ctx.goldenArtifactRun!.runId,
        generatedAt: new Date().toISOString(),
        entries: jsonSafeClone(entries),
      },
      traceSubdirectory ? { subdirectory: traceSubdirectory } : undefined
    );
  });
}

async function captureGoldenToolCall(input: {
  ctx?: AgentToolContext;
  subdirectory?: string;
  sequence?: number;
  loopIndex?: number;
  callIndexInLoop: number;
  callId: string;
  toolName: string;
  rawArguments?: string;
  parsedToolCall?: ToolCall;
  formattedArgs?: string;
  blockedReason?: string;
  parseError?: string;
}): Promise<void> {
  const { ctx, subdirectory } = input;
  if (!ctx?.goldenArtifactRun || !subdirectory) return;
  await runGoldenCaptureStep(ctx.goldenArtifactRun, ctx.log ?? (() => {}), `${subdirectory}/tool_call.json`, async () => {
    await uploadGoldenArtifactJson(
      ctx.goldenArtifactRun!.runId,
      "tool_call",
      jsonSafeClone({
        runId: ctx.goldenArtifactRun!.runId,
        generatedAt: new Date().toISOString(),
        sequence: input.sequence,
        loopIndex: input.loopIndex,
        callIndexInLoop: input.callIndexInLoop,
        callId: input.callId,
        toolName: input.toolName,
        rawArguments: input.rawArguments ?? "",
        parsedToolCall: input.parsedToolCall,
        formattedArgs: input.formattedArgs,
        blockedReason: input.blockedReason,
        parseError: input.parseError,
      }),
      { subdirectory }
    );
  });
}

async function captureGoldenToolOutput(input: {
  ctx?: AgentToolContext;
  subdirectory?: string;
  sequence?: number;
  loopIndex?: number;
  callIndexInLoop: number;
  callId: string;
  toolName: string;
  toolResult: ToolResult;
  normalizedFunctionOutput: Record<string, unknown>;
  functionCallOutput: NativeFunctionCallOutputItem;
  referenceImages?: ReferenceSlideImage[];
}): Promise<void> {
  const { ctx, subdirectory } = input;
  if (!ctx?.goldenArtifactRun || !subdirectory) return;
  const capturedToolResult = input.toolResult;
  const capturedNormalizedOutput = input.normalizedFunctionOutput;
  const capturedFunctionOutput = input.functionCallOutput;
  await runGoldenCaptureStep(
    ctx.goldenArtifactRun,
    ctx.log ?? (() => {}),
    `${subdirectory}/tool_output.json`,
    async () => {
      await uploadGoldenArtifactJson(
        ctx.goldenArtifactRun!.runId,
        "tool_output",
        jsonSafeClone({
          runId: ctx.goldenArtifactRun!.runId,
          generatedAt: new Date().toISOString(),
          sequence: input.sequence,
          loopIndex: input.loopIndex,
          callIndexInLoop: input.callIndexInLoop,
          callId: input.callId,
          toolName: input.toolName,
          toolResult: capturedToolResult,
          normalizedFunctionOutput: capturedNormalizedOutput,
          functionCallOutput: capturedFunctionOutput,
          referenceImages: buildReferenceImageArtifacts(input.referenceImages),
        }),
        { subdirectory }
      );
    }
  );
}

export function buildInitialUserInput(
  baseText: string,
  slideImages?: string[],
  referenceExamples?: ReferenceDeckExamples
): unknown {
  const hasSlideImages = Boolean(slideImages && slideImages.length > 0);
  const hasReference = Boolean(referenceExamples && referenceExamples.slides.length > 0);
  if (!hasSlideImages && !hasReference) {
    return baseText;
  }

  const contentParts: unknown[] = [];

  // Leading images (legacy slideImages callers) keep the images-first placement.
  if (hasSlideImages) {
    const imageUrls = slideImages!
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl));
    for (const imageUrl of imageUrls) {
      contentParts.push({
        type: "input_image",
        image_url: imageUrl,
        detail: imageUrls.length > 2 ? "low" : "auto",
      });
    }
  }

  contentParts.push({ type: "input_text", text: baseText });

  // Reference examples INTERLEAVED: each slide's JSON spec is immediately
  // followed by that slide's rendered image, binding spec-to-visual per slide.
  if (hasReference) {
    const ref = referenceExamples!;
    const imageCount = ref.slides.filter((slide) => slide.image).length;
    const detail = imageCount > 2 ? "low" : "auto";
    contentParts.push({ type: "input_text", text: ref.intro });
    for (const slide of ref.slides) {
      // Curated references carry an authored caption instead of a shape dump.
      const spec = [slide.caption, slide.json].filter(Boolean).join("\n");
      contentParts.push({
        type: "input_text",
        text: spec ? `${slide.label}\n${spec}` : slide.label,
      });
      const imageUrl = slide.image ? buildOpenAIImageUrl(slide.image) : null;
      if (imageUrl) {
        contentParts.push({ type: "input_image", image_url: imageUrl, detail });
      }
    }
    contentParts.push({ type: "input_text", text: ref.outro });
  }

  return [{ role: "user", content: contentParts }];
}

function normalizeInputToConversationHistory(input: unknown): unknown[] {
  if (Array.isArray(input)) {
    return [...input];
  }
  if (typeof input === "string") {
    return [{ role: "user", content: input }];
  }
  return [{ role: "user", content: String(input ?? "") }];
}

export function prependUserMessages(
  input: unknown,
  messages: readonly string[] = []
): unknown[] {
  return [
    ...messages
      .filter((message) => message.trim().length > 0)
      .map((content) => ({ role: "user", content })),
    ...normalizeInputToConversationHistory(input),
  ];
}

function applyPreludeUserMessages(
  input: unknown,
  messages: readonly string[] | undefined
): unknown {
  if (!messages?.some((message) => message.trim().length > 0)) {
    return input;
  }
  return prependUserMessages(input, messages);
}

function sanitizeOutputItemForReplay(item: OpenAIResponseOutputItem): Record<string, unknown> {
  const cloned = JSON.parse(JSON.stringify(item)) as Record<string, unknown>;
  if (isNativeFunctionCallType(cloned.type)) {
    cloned.type = "function_call";
    const callId = extractFunctionCallIdFromItem(cloned);
    if (callId) {
      cloned.call_id = callId;
    }
    cloned.name = extractFunctionNameFromItem(cloned);
    cloned.arguments = extractFunctionArgumentsFromItem(cloned);
    delete cloned.function;
    delete cloned.tool_call_id;
  }
  delete cloned.id;
  return cloned;
}

function appendResponseOutputToConversationHistory(
  conversationHistory: unknown[],
  data: OpenAIResponsePayload
): unknown[] {
  const outputItems = Array.isArray(data.output) ? data.output : [];
  if (outputItems.length === 0) {
    return conversationHistory;
  }
  const sanitizedOutputItems = outputItems.map((item) => sanitizeOutputItemForReplay(item));
  return [...conversationHistory, ...sanitizedOutputItems];
}

function buildFollowUpInput(
  conversationReplay: unknown[],
  slideImages?: string[]
): unknown[] {
  const inputItems: unknown[] = [...conversationReplay];
  if (slideImages && slideImages.length > 0) {
    const contentParts: unknown[] = slideImages
      .map((img) => buildOpenAIImageUrl(img))
      .filter((imageUrl): imageUrl is string => Boolean(imageUrl))
      .map((imageUrl) => ({
        type: "input_image",
        image_url: imageUrl,
        detail: slideImages.length > 2 ? "low" : "auto",
      }));
    contentParts.push({
      type: "input_text",
      text: "Below are rendered screenshot(s) from this turn showing the latest available state of the relevant slide(s). They are included as visual context for the current deck state.",
    });
    inputItems.push({ role: "user", content: contentParts });
  }
  return inputItems;
}

/**
 * Raw OpenAI Responses streaming event (a parsed `data:` SSE frame). Permissive
 * by design — only the fields the transport + UI observer read are typed.
 */
export interface OpenAIResponsesStreamEvent {
  type?: string;
  delta?: string;
  item_id?: string;
  output_index?: number;
  content_index?: number;
  arguments?: string;
  text?: string;
  item?: {
    id?: string;
    type?: string;
    name?: string;
    call_id?: string;
    arguments?: string;
  };
  response?: OpenAIResponsePayload & { error?: { message?: string } };
  error?: { message?: string };
}

/** Office task-pane webviews (WebView2 / modern WebKit) support fetch streaming. */
function responsesStreamingEnabled(): boolean {
  return typeof ReadableStream !== "undefined";
}

/**
 * Reads an OpenAI Responses SSE stream. Forwards every parsed event to
 * `onStreamEvent` (for live UI) and returns the terminal `response.completed`
 * payload, which is identical to the non-streaming `response.json()` body — so
 * all downstream parsing (function calls, structured output, golden capture) is
 * unchanged. The observer can never break the stream.
 */
async function readResponsesStream(
  body: ReadableStream<Uint8Array>,
  requestLabel: string,
  onStreamEvent?: (event: OpenAIResponsesStreamEvent) => void
): Promise<OpenAIResponsePayload> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = "";
  let completed: OpenAIResponsePayload | null = null;
  let streamError: string | null = null;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split("\n");
      sseBuffer = lines.pop() || "";
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":") || trimmed.startsWith("event:")) continue;
        if (!trimmed.startsWith("data:")) continue;
        const dataStr = trimmed.slice(5).trim();
        if (!dataStr || dataStr === "[DONE]") continue;
        let event: OpenAIResponsesStreamEvent;
        try {
          event = JSON.parse(dataStr) as OpenAIResponsesStreamEvent;
        } catch {
          continue;
        }
        const type = typeof event.type === "string" ? event.type : "";
        if (type === "response.completed" || type === "response.incomplete") {
          if (event.response) completed = event.response;
        } else if (type === "response.failed" || type === "error") {
          streamError = event.response?.error?.message || event.error?.message || "stream failed";
        }
        if (onStreamEvent) {
          try {
            onStreamEvent(event);
          } catch {
            // An observer error must never abort the model stream.
          }
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // reader may already be released
    }
  }

  if (streamError) {
    throw new Error(`${requestLabel} stream error: ${streamError}`);
  }
  if (!completed) {
    throw new Error(`${requestLabel} stream ended without a completed response`);
  }
  return completed;
}

async function sendOpenAIRequest(
  requestBody: Record<string, unknown>,
  requestLabel: string,
  retries: number,
  options: AiRequestOptions = {},
  onStreamEvent?: (event: OpenAIResponsesStreamEvent) => void,
  phaseOverride?: PptxLangfusePhase
): Promise<OpenAIResponsePayload> {
  const phase = phaseOverride ?? resolveSeedPhase(options);
  const requestBodyJson = JSON.stringify(requestBody);
  const response = await fetchWithRetry(
    buildApiUrl("/api/ai/openai/responses"),
    withRequestLangfuse(
      withCredentials({
        method: "POST",
        body: requestBodyJson,
        signal: options.signal,
      }),
      options,
      phase,
      buildOpenAIRequestLangfuseExtra(requestBody, requestLabel)
    ),
    retries,
    requestLabel
  );

  if (!response.ok) {
    throw new Error(await readModelError(response, `${requestLabel} request failed`));
  }

  // Streaming path: parse SSE and reconstruct the canonical payload from
  // `response.completed`. `fetchWithRetry` only retries the initial connection,
  // so once bytes flow there is no mid-stream retry. Falls back to JSON when the
  // upstream did not actually stream (ReadableStream unavailable, or a non-SSE
  // body slipped through).
  const contentType = response.headers?.get("content-type") ?? "";
  if (requestBody.stream === true && response.body && contentType.includes("text/event-stream")) {
    return readResponsesStream(response.body, requestLabel, onStreamEvent);
  }

  return (await response.json()) as OpenAIResponsePayload;
}

function maybeEmitThinking(
  data: OpenAIResponsePayload,
  callbacks: NativeLoopCallbacks
): void {
  const reasoning = extractReasoningContent(data);
  // Diagnostic for "Thought process accordion is missing on completed
  // messages". If this logs `chars=0` on every native round-trip even
  // though `reasoning: { summary: "auto" }` is set in the request, the
  // model isn't returning summary_text items — that's a model/backend
  // issue, not a UI gate. If it logs non-zero but the assistant message
  // still has `thinkingChars=0` in `[CHAT] assistantMessage`, the
  // `setStreamingThinking → latestStreamingThinking` mirror is the
  // failing link.
  console.log(`[OPENAI_NATIVE] reasoning emit chars=${reasoning.length}`);
  if (reasoning && callbacks.onThinking) {
    callbacks.onThinking(reasoning);
  }
}

function parseToolCallArguments(rawArguments: string): Record<string, unknown> {
  const parsed = JSON.parse(rawArguments);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Tool arguments must be a JSON object");
  }
  return parsed as Record<string, unknown>;
}

function requireNonEmptyStringArgument(args: Record<string, unknown>, key: string): string {
  const value = args[key];
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`"${key}" must be a non-empty string`);
  }
  return value.trim();
}

function requireNonEmptyStringArrayArgument(args: Record<string, unknown>, key: string): string[] {
  const value = args[key];
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(`"${key}" must be a non-empty array of strings`);
  }
  return value.map((item) => {
    if (typeof item !== "string" || item.trim().length === 0) {
      throw new Error(`"${key}" must be a non-empty array of strings`);
    }
    return item.trim();
  });
}

function requirePositiveNumberArgument(
  args: Record<string, unknown>,
  key: string,
  options: { integer?: boolean } = {}
): number {
  const value = args[key];
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`"${key}" must be a positive number`);
  }
  return options.integer ? Math.floor(value) : value;
}

function parseAndValidateNativeToolCall(
  toolName: ToolCall["tool"],
  parsedArgs: Record<string, unknown>
): ToolCall {
  switch (toolName) {
    case "finster_query":
    case "factset_ai_query": {
      const query = requireNonEmptyStringArgument(parsedArgs, "query");
      // queryMode is required by the schema; fall back to "thinking" (deeper)
      // for any missing/invalid value to preserve answer quality.
      const queryMode = parsedArgs.queryMode === "standard" ? "standard" : "thinking";
      return {
        tool: toolName,
        query,
        queryMode,
      };
    }
    case "get_company_logo": {
      const companyNames = requireNonEmptyStringArrayArgument(parsedArgs, "company_names");
      const toolCall: ToolCall = {
        tool: "get_company_logo",
        company_names: companyNames,
      };
      if (parsedArgs.size !== undefined) {
        toolCall.size = requirePositiveNumberArgument(parsedArgs, "size", { integer: true });
      }
      return toolCall;
    }
    case "active_graph": {
      const operation = requireNonEmptyStringArgument(parsedArgs, "operation");
      const allowedOperations = new Set([
        "create_active_graph",
        "inspect_selected_active_graph",
        "list_active_graph_stylesets",
        "update_active_graph_frame",
        "delete_selected_active_graph",
      ]);
      if (!allowedOperations.has(operation)) {
        throw new Error(`Unsupported active_graph operation "${operation}"`);
      }
      return {
        tool: "active_graph",
        activeGraphInput: {
          ...parsedArgs,
          operation,
        } as unknown as NonNullable<ToolCall["activeGraphInput"]>,
      };
    }
    case "list_active_graph_types":
      return { tool: "list_active_graph_types" };
    case "id_lookup":
      return {
        tool: "id_lookup",
        idLookupSearchText: requireNonEmptyStringArgument(parsedArgs, "search_text"),
      };
    case "get_slide_images": {
      const slideNumbersRaw = parsedArgs.slide_numbers;
      if (!Array.isArray(slideNumbersRaw) || slideNumbersRaw.length === 0) {
        throw new Error(`"slide_numbers" must be a non-empty array of non-negative numbers`);
      }
      const slideNumbers = slideNumbersRaw.map((value) => {
        if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
          throw new Error(`"slide_numbers" must be a non-empty array of non-negative numbers`);
        }
        return Math.floor(value);
      });
      const toolCall: ToolCall = {
        tool: "get_slide_images",
        slide_numbers: slideNumbers,
      };
      if (parsedArgs.max_images !== undefined) {
        toolCall.max_images = requirePositiveNumberArgument(parsedArgs, "max_images", {
          integer: true,
        });
      }
      return toolCall;
    }
    case "apply_edits": {
      const mods = parsedArgs.modifications;
      if (!mods || typeof mods !== "object" || Array.isArray(mods)) {
        throw new Error(`"modifications" must be a PresentationModification object`);
      }
      const toolCall: ToolCall = {
        tool: "apply_edits",
        modifications: mods as unknown as PresentationModification,
      };
      const ret = parsedArgs.return;
      if (ret && typeof ret === "object" && !Array.isArray(ret)) {
        const flags = ret as Record<string, unknown>;
        if (flags.json === true) toolCall.returnJson = true;
        if (flags.image === true) toolCall.returnImage = true;
      }
      return toolCall;
    }
    case "get_slides_info": {
      const slideNumbersRaw = parsedArgs.slide_numbers;
      const wantsLayoutMetadata =
        parsedArgs.layouts === true ||
        parsedArgs.designs === true ||
        parsedArgs.master_shapes === true ||
        Array.isArray(parsedArgs.layout_names);
      if (
        (!Array.isArray(slideNumbersRaw) || slideNumbersRaw.length === 0) &&
        !wantsLayoutMetadata
      ) {
        throw new Error(
          `"slide_numbers" must be a non-empty array of non-negative numbers unless requesting layout/design metadata`
        );
      }
      const slideNumbers = Array.isArray(slideNumbersRaw)
        ? slideNumbersRaw.map((value) => {
            if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
              throw new Error(`"slide_numbers" must contain only non-negative numbers`);
            }
            return Math.floor(value);
          })
        : [];
      const toolCall: ToolCall = { tool: "get_slides_info", slide_numbers: slideNumbers };
      if (parsedArgs.json === true) toolCall.returnJson = true;
      if (parsedArgs.image === true) toolCall.returnImage = true;
      if (parsedArgs.layouts === true) toolCall.returnLayouts = true;
      if (Array.isArray(parsedArgs.layout_names)) {
        toolCall.layout_names = parsedArgs.layout_names.filter(
          (value): value is string => typeof value === "string" && value.trim().length > 0
        );
      }
      if (parsedArgs.designs === true) toolCall.returnDesigns = true;
      if (parsedArgs.master_shapes === true) toolCall.returnMasterShapes = true;
      return toolCall;
    }
    case "insert_library_slide": {
      const toolCall: ToolCall = {
        tool: "insert_library_slide",
        librarySlideId: requireNonEmptyStringArgument(parsedArgs, "library_slide_id"),
      };
      const afterSlideId = parsedArgs.after_slide_id;
      if (typeof afterSlideId === "string" && afterSlideId.trim().length > 0) {
        toolCall.afterSlideId = afterSlideId.trim();
      }
      return toolCall;
    }
    case "apply_preslink": {
      const operation =
        parsedArgs.operation === "refresh" || parsedArgs.operation === "inspect"
          ? parsedArgs.operation
          : parsedArgs.process_presentation_file === true
            ? "refresh"
            : "inspect";
      const shapesRaw = parsedArgs.shapes;
      if (!Array.isArray(shapesRaw) || shapesRaw.length === 0) {
        throw new Error(`"shapes" must be a non-empty array of { slide_id, shape_id } objects`);
      }
      const preslinkShapeRefs = shapesRaw.map((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
          throw new Error(`each "shapes" entry must be an object with slide_id and shape_id`);
        }
        const record = entry as Record<string, unknown>;
        const slideId = typeof record.slide_id === "string" ? record.slide_id.trim() : "";
        const id = typeof record.shape_id === "string" ? record.shape_id.trim() : "";
        if (!slideId || !id) {
          throw new Error(`each "shapes" entry requires non-empty slide_id and shape_id strings`);
        }
        return { slideId, id };
      });
      const toolCall: ToolCall = {
        tool: "apply_preslink",
        preslinkOperation: operation,
        preslinkShapeRefs,
      };
      if (parsedArgs.include_ppt_file === true) toolCall.includePptFile = true;
      if (parsedArgs.include_cell_data === false) toolCall.includeCellData = false;
      if (operation === "refresh") toolCall.processPresentationFile = true;
      if (parsedArgs.scope === "deck" || parsedArgs.scope === "shapes") {
        toolCall.preslinkScope = parsedArgs.scope;
      }
      return toolCall;
    }
    case "list_slide_library_collections":
      return { tool: "list_slide_library_collections" };
    case "search_slide_library": {
      const toolCall: ToolCall = { tool: "search_slide_library" };
      const collectionIds: string[] = [];
      if (Array.isArray(parsedArgs.collection_ids)) {
        for (const value of parsedArgs.collection_ids) {
          if (typeof value === "string" && value.trim()) {
            collectionIds.push(value.trim());
          }
        }
      }
      const collectionId = parsedArgs.collection_id;
      if (typeof collectionId === "string" && collectionId.trim()) {
        collectionIds.push(collectionId.trim());
      }
      if (collectionIds.length > 0) {
        toolCall.collectionIds = [...new Set(collectionIds)];
      }
      if (parsedArgs.scope === "all") {
        toolCall.slideLibraryScope = "all";
      }
      if (Array.isArray(parsedArgs.tags)) {
        toolCall.tags = parsedArgs.tags.filter(
          (value): value is string => typeof value === "string" && value.trim().length > 0
        );
      }
      if (typeof parsedArgs.offset === "number" && Number.isFinite(parsedArgs.offset)) {
        toolCall.offset = Math.max(0, Math.floor(parsedArgs.offset));
      }
      if (typeof parsedArgs.limit === "number" && Number.isFinite(parsedArgs.limit)) {
        toolCall.limit = Math.min(200, Math.max(1, Math.floor(parsedArgs.limit)));
      }
      return toolCall;
    }
    case "get_library_slide_previews":
      return {
        tool: "get_library_slide_previews",
        slideIds: requireNonEmptyStringArrayArgument(parsedArgs, "slide_ids").slice(0, 200),
      };
    case "present_deck_plan":
      return {
        tool: "present_deck_plan",
        deckPlan: parseDeckPlanFromToolArgs(parsedArgs),
      };
    case "update_deck_plan": {
      const toolCall: ToolCall = { tool: "update_deck_plan" };
      if (Array.isArray(parsedArgs.slides) && parsedArgs.slides.length > 0) {
        toolCall.deckPlan = parseDeckPlanFromToolArgs(parsedArgs, "Current plan");
      } else if (typeof parsedArgs.topic === "string" && parsedArgs.topic.trim()) {
        toolCall.deckPlan = {
          topic: parsedArgs.topic.trim(),
          slideCount: 0,
          slides: [],
        };
      }
      if (Array.isArray(parsedArgs.patches)) {
        toolCall.deckPlanPatches = parsedArgs.patches
          .map((patch, index) => parseSlideOutlinePatch(patch, index))
          .filter((slide): slide is NonNullable<typeof slide> => Boolean(slide));
      }
      if (!toolCall.deckPlan && !toolCall.deckPlanPatches?.length) {
        throw new Error(`update_deck_plan requires "slides" or "patches"`);
      }
      return toolCall;
    }
    default:
      return {
        tool: toolName,
      };
  }
}

async function handleNativeFunctionCalls(
  calls: NativeFunctionCall[],
  presentationContent: PresentationContent,
  callbacks: NativeLoopCallbacks,
  toolPolicy?: ToolExecutionPolicy,
  agentToolContext?: AgentToolContext,
  loopIndex?: number,
  goldenToolTraceEntries: GoldenToolTraceEntry[] = [],
  langfuseContext?: AiRequestOptions["langfuseContext"]
): Promise<NativeToolHandlingResult> {
  type PendingNativeCall = {
    sequence: number;
    callId: string;
    tool: string;
    callIndexInLoop: number;
    rawArguments: string;
    goldenSubdirectory?: string;
    parsedToolCall?: ToolCall;
    formattedArgs?: string;
    parseError?: string;
    blockedReason?: string;
    referenceImages?: ReferenceSlideImage[];
  };

  const pendingCalls: PendingNativeCall[] = [];
  const executableToolCalls: ToolCall[] = [];
  const executableGoldenSubdirectories: Array<string | undefined> = [];
  const executableCallIds: string[] = [];

  for (let callIndex = 0; callIndex < calls.length; callIndex += 1) {
    const call = calls[callIndex];
    const goldenSubdirectory =
      agentToolContext?.goldenArtifactRun && agentToolContext.nextGoldenToolSubdirectory
        ? agentToolContext.nextGoldenToolSubdirectory(call.name)
        : undefined;
    const sequence = goldenToolTraceEntries.length;
    goldenToolTraceEntries.push({
      sequence,
      loopIndex,
      callIndexInLoop: callIndex,
      callId: call.callId,
      toolName: call.name,
      subdirectory: goldenSubdirectory,
      status: "pending",
      files: ["tool_call.json"],
    });
    const pending: PendingNativeCall = {
      sequence,
      callId: call.callId,
      tool: call.name,
      callIndexInLoop: callIndex,
      rawArguments: call.rawArguments,
      goldenSubdirectory,
    };

    if (!OPENAI_FUNCTION_TOOL_NAMES.has(call.name as ToolCall["tool"])) {
      pending.parseError = `Unsupported tool name: ${call.name}`;
      pendingCalls.push(pending);
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        parseError: pending.parseError,
      });
      continue;
    }

    if (!isToolAllowed(call.name, toolPolicy)) {
      pending.blockedReason = `Tool ${call.name} is disabled for this request context.`;
      pendingCalls.push(pending);
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        blockedReason: pending.blockedReason,
      });
      continue;
    }

    try {
      const parsedArgs = parseToolCallArguments(call.rawArguments);
      const parsedToolCall = parseAndValidateNativeToolCall(
        call.name as ToolCall["tool"],
        parsedArgs
      );
      pending.parsedToolCall = parsedToolCall;
      pending.formattedArgs = formatToolCallArgs(parsedToolCall);
      pendingCalls.push(pending);
      executableToolCalls.push(parsedToolCall);
      executableGoldenSubdirectories.push(goldenSubdirectory);
      executableCallIds.push(call.callId);
      callbacks.onToolCall?.(parsedToolCall.tool, pending.formattedArgs);
      callbacks.onAgentEvent?.({
        kind: "tool_call",
        hop: loopIndex ?? 0,
        callId: call.callId,
        tool: parsedToolCall.tool,
        argsPreview: pending.formattedArgs,
        status: "calling",
      });
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        parsedToolCall,
        formattedArgs: pending.formattedArgs,
      });
    } catch (error) {
      pending.parseError = `Malformed tool arguments: ${error instanceof Error ? error.message : String(error)}`;
      pendingCalls.push(pending);
      await captureGoldenToolCall({
        ctx: agentToolContext,
        subdirectory: goldenSubdirectory,
        sequence,
        loopIndex,
        callIndexInLoop: callIndex,
        callId: call.callId,
        toolName: call.name,
        rawArguments: call.rawArguments,
        parseError: pending.parseError,
      });
    }
  }

  const executed =
    executableToolCalls.length > 0
      ? await executeTools(executableToolCalls, {
          presentationContent,
          finsterDataSources: toolPolicy?.finsterDataSources,
          toolPolicy,
          agentToolContext,
          langfuseContext,
          onToolProgress: callbacks.onAgentEvent
            ? (toolCallIndex, message) => {
                const callId = executableCallIds[toolCallIndex];
                const tool = executableToolCalls[toolCallIndex]?.tool;
                if (!callId || !tool) return;
                callbacks.onAgentEvent?.({
                  kind: "tool_progress",
                  hop: loopIndex ?? 0,
                  callId,
                  tool,
                  text: message,
                });
              }
            : undefined,
          onToolEvent: callbacks.onAgentEvent
            ? (toolCallIndex, event) => {
                const onAgentEvent = callbacks.onAgentEvent;
                if (!onAgentEvent) return;
                const callId = executableCallIds[toolCallIndex];
                if (!callId) return;
                const hop = loopIndex ?? 0;
                switch (event.type) {
                  case "reasoning":
                    onAgentEvent({
                      kind: "subagent_reasoning_delta",
                      hop,
                      callId,
                      subHop: event.hopIndex,
                      text: event.chunk,
                    });
                    break;
                  case "tool":
                    onAgentEvent({
                      kind: "subagent_tool_call",
                      hop,
                      callId,
                      subHop: event.hopIndex,
                      subCallId: event.callId || `${event.toolName}-${event.hopIndex}`,
                      tool: event.toolName,
                      status: event.status,
                      description: event.description,
                      sectionTitle: event.sectionTitle,
                    });
                    break;
                  case "answerDelta":
                    onAgentEvent({ kind: "subagent_output_delta", hop, callId, text: event.chunk });
                    break;
                  case "final":
                    onAgentEvent({
                      kind: "subagent_result",
                      hop,
                      callId,
                      answer: event.answer,
                      citations: event.citations,
                    });
                    break;
                  case "status":
                  case "session":
                    break;
                }
              }
            : undefined,
          ...(executableGoldenSubdirectories.some(Boolean)
            ? { goldenSubdirectories: executableGoldenSubdirectories }
            : {}),
        })
      : {
          results: [] as ToolResult[],
          referenceImages: [] as ReferenceSlideImage[],
          referenceImagesByResultIndex: [] as Array<ReferenceSlideImage[] | undefined>,
        };

  const toolResults: ToolResult[] = [];
  let executedIndex = 0;
  for (const pending of pendingCalls) {
    if (pending.blockedReason) {
      toolResults.push(buildBlockedToolResult(pending.tool, pending.blockedReason));
      continue;
    }
    if (pending.parseError) {
      toolResults.push({
        tool: pending.tool,
        success: false,
        error: pending.parseError,
      });
      continue;
    }
    const executedResult = executed.results[executedIndex];
    if (!executedResult) {
      toolResults.push({
        tool: pending.tool,
        success: false,
        error: "Tool execution result missing from runtime.",
      });
    } else {
      toolResults.push(executedResult);
      pending.referenceImages = executed.referenceImagesByResultIndex?.[executedIndex];
    }
    executedIndex += 1;
  }

  callbacks.onToolResult?.(toolResults);
  if (callbacks.onAgentEvent) {
    for (let resultIndex = 0; resultIndex < pendingCalls.length; resultIndex += 1) {
      const pendingCall = pendingCalls[resultIndex];
      const toolResult = toolResults[resultIndex];
      if (!pendingCall || !toolResult) continue;
      callbacks.onAgentEvent({
        kind: "tool_result",
        hop: loopIndex ?? 0,
        callId: pendingCall.callId,
        tool: toolResult.tool,
        ok: toolResult.success,
        result: toolResult,
      });
    }
  }

  const normalizedFunctionOutputs = toolResults.map((result) => normalizeToolResultForOutput(result));
  const functionCallOutputs: NativeFunctionCallOutputItem[] = pendingCalls.map((pending, index) => ({
    type: "function_call_output",
    call_id: pending.callId,
    output: JSON.stringify(normalizedFunctionOutputs[index]),
  }));

  for (let index = 0; index < pendingCalls.length; index += 1) {
    const pending = pendingCalls[index];
    const toolResult = toolResults[index];
    const traceEntry = goldenToolTraceEntries[pending.sequence];
    if (traceEntry) {
      traceEntry.status = resolveTraceStatus({ pending, toolResult });
      traceEntry.success = toolResult?.success;
      traceEntry.error = toolResult?.error;
      traceEntry.files = buildToolTraceFiles(pending.tool);
      traceEntry.requestedReturn = buildRequestedReturn(pending.parsedToolCall);
      traceEntry.referenceImageCount = pending.referenceImages?.length ?? 0;
      traceEntry.hasSlidesInfo = hasSlidesInfo(toolResult);
      traceEntry.createdSlideCount = getCreatedSlideCount(toolResult);
    }
    await captureGoldenToolOutput({
      ctx: agentToolContext,
      subdirectory: pending.goldenSubdirectory,
      sequence: pending.sequence,
      loopIndex,
      callIndexInLoop: pending.callIndexInLoop,
      callId: pending.callId,
      toolName: pending.tool,
      toolResult,
      normalizedFunctionOutput: normalizedFunctionOutputs[index],
      functionCallOutput: functionCallOutputs[index],
      referenceImages: pending.referenceImages,
    });
  }

  return {
    functionCallOutputs,
    toolResults,
    referenceImages: executed.referenceImages,
  };
}

function createNativeFollowUpRequestBody(params: {
  model: ModelConfig;
  systemPrompt: string;
  tools: OpenAIFunctionToolDefinition[];
  input: unknown[];
  toolPolicy?: ToolExecutionPolicy;
}): Record<string, unknown> {
  return {
    model: params.model.model,
    instructions: params.systemPrompt,
    input: params.input,
    // See note on `queryOpenAILegacy` re: `summary: "auto"`.
    reasoning: { effort: "low", summary: "auto" },
    tools: params.tools,
    tool_choice: "auto",
    text: { format: buildOpenAIModificationResponseFormat(params.toolPolicy) },
    store: false,
    stream: responsesStreamingEnabled(),
    service_tier: "priority",
  };
}

function createNativeForcedFinalizationRequestBody(params: {
  model: ModelConfig;
  systemPrompt: string;
  tools: OpenAIFunctionToolDefinition[];
  input: unknown[];
  toolPolicy?: ToolExecutionPolicy;
}): Record<string, unknown> {
  return {
    model: params.model.model,
    instructions: params.systemPrompt,
    input: params.input,
    reasoning: { effort: "low", summary: "auto" },
    tools: params.tools,
    tool_choice: "none",
    text: { format: buildOpenAIModificationResponseFormat(params.toolPolicy) },
    store: false,
    stream: responsesStreamingEnabled(),
    service_tier: "priority",
  };
}

export async function continueOpenAIWithFunctionOutputs(
  options: ContinueOpenAIWithFunctionOutputsOptions
): Promise<ContinueOpenAIWithFunctionOutputsResult> {
  const tools = buildOpenAINativeTools(options.toolPolicy);
  let followUpConversationReplay: unknown[] = [
    ...options.conversationHistory,
    ...options.functionCallOutputs,
  ];
  const replayState = collectReplayFunctionCallState(followUpConversationReplay);
  if (replayState.missingCallIdCount > 0) {
    recordOpenAINativeMetric("tool_loop_failure", {
      reason: "missing_call_id",
      stage: "followup",
      missingCallIdCount: replayState.missingCallIdCount,
    });
    throw new Error(FUNCTION_CALL_MISSING_ID_ERROR);
  }
  if (replayState.unresolvedCalls.length > 0) {
    const reconciliation = buildFallbackOutputsForUnansweredCalls(
      replayState.unresolvedCalls,
      FUNCTION_CALL_NO_OUTPUT_ERROR
    );
    recordOpenAINativeMetric("tool_loop_replay_reconciled", {
      stage: "followup",
      unresolvedCalls: replayState.unresolvedCalls.length,
    });
    followUpConversationReplay = [...followUpConversationReplay, ...reconciliation.fallbackOutputs];
  }
  const followUpInput = buildFollowUpInput([...followUpConversationReplay], options.slideImages);
  const responseData = await sendOpenAIRequest(
    createNativeFollowUpRequestBody({
      model: options.model,
      systemPrompt: options.systemPrompt,
      tools,
      input: followUpInput,
      toolPolicy: options.toolPolicy,
    }),
    options.loopLabel || "OPENAI-NATIVE-FOLLOWUP",
    2,
    {
      signal: options.signal,
      langfuseContext: options.langfuseContext,
      preludeUserMessages: options.preludeUserMessages,
    },
    options.onStreamEvent,
    "multi_hop_executor"
  );
  const conversationHistory = appendResponseOutputToConversationHistory(
    followUpConversationReplay,
    responseData
  );
  return {
    responseData,
    conversationHistory,
  };
}

async function queryOpenAINative(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const systemPrompt = getSystemPrompt(toolPolicy);
  const { textMessage } = prepareRequestData(presentationContent, userQuery, chatHistory);
  const tools = buildOpenAINativeTools(toolPolicy);
  const initialInput = buildInitialUserInput(textMessage, slideImages, options.referenceExamples);
  let conversationHistory = prependUserMessages(initialInput, options.preludeUserMessages);
  const requestBody = {
    model: model.model,
    instructions: systemPrompt,
    input: conversationHistory,
    // See note on `queryOpenAILegacy` re: `summary: "auto"`.
    reasoning: { effort: "low", summary: "auto" },
    tools,
    // The main agent owns PresLink intent: it decides from the request whether a
    // refresh or inspection is warranted, so no tool is ever force-picked here.
    tool_choice: "auto",
    text: { format: buildOpenAIModificationResponseFormat(toolPolicy) },
    store: false,
    stream: responsesStreamingEnabled(),
    service_tier: "priority",
  };

  const t0 = Date.now();
  const onAgentEvent = callbacks.onAgentEvent;
  const includeApplyEditArgsDeltas = options.streamApplyEditSlideSummaries === true;
  const makeHopObserver = onAgentEvent
    ? (hop: number) => (event: OpenAIResponsesStreamEvent) => {
        for (const mapped of mapOpenAIStreamEvent(event, hop, { includeApplyEditArgsDeltas })) {
          onAgentEvent(mapped);
        }
      }
    : (_hop: number) => undefined;
  let lastHopIndex = 0;
  try {
    let responseData = await sendOpenAIRequest(
      requestBody,
      "OPENAI-NATIVE",
      3,
      options,
      makeHopObserver(0)
    );
    conversationHistory = appendResponseOutputToConversationHistory(conversationHistory, responseData);
    let referenceImages: string[] | undefined;
    const latestReferenceImagesBySlideId = new Map<string, string>();
    let terminatedByNoFunctionCalls = false;
    const effectiveLoopLimit = Math.max(1, getOpenAINativeToolLoopLimit());
    let totalToolCalls = 0;
    const goldenToolTraceEntries: GoldenToolTraceEntry[] = [];

    for (let loopIndex = 0; loopIndex < effectiveLoopLimit; loopIndex += 1) {
      maybeEmitThinking(responseData, callbacks);
      const extractedFunctionCalls = extractNativeFunctionCallsWithDiagnostics(responseData);
      if (extractedFunctionCalls.missingCallIdCount > 0) {
        recordOpenAINativeMetric("tool_loop_failure", {
          reason: "missing_call_id",
          stage: "loop",
          missingCallIdCount: extractedFunctionCalls.missingCallIdCount,
        });
        callbacks.onError(FUNCTION_CALL_MISSING_ID_ERROR);
        return;
      }
      const functionCalls = extractedFunctionCalls.calls;
      if (functionCalls.length === 0) {
        terminatedByNoFunctionCalls = true;
        break;
      }
      totalToolCalls += functionCalls.length;

      const handledCalls = await handleNativeFunctionCalls(
        functionCalls,
        presentationContent,
        callbacks,
        toolPolicy,
        options.agentToolContext,
        loopIndex,
        goldenToolTraceEntries,
        options.langfuseContext
      );
      await captureGoldenToolTrace(options.agentToolContext, goldenToolTraceEntries);
      if (handledCalls.referenceImages.length > 0) {
        referenceImages = updateLatestReferenceImagesBySlide(
          latestReferenceImagesBySlideId,
          handledCalls.referenceImages,
          16
        );
      }

      lastHopIndex = loopIndex + 1;
      const continuation = await continueOpenAIWithFunctionOutputs({
        model,
        systemPrompt,
        toolPolicy,
        conversationHistory,
        functionCallOutputs: handledCalls.functionCallOutputs,
        slideImages: referenceImages,
        loopLabel: `OPENAI-NATIVE-LOOP-${loopIndex + 1}`,
        signal: options.signal,
        onStreamEvent: makeHopObserver(loopIndex + 1),
        langfuseContext: options.langfuseContext,
        preludeUserMessages: options.preludeUserMessages,
      });
      responseData = continuation.responseData;
      conversationHistory = continuation.conversationHistory;

      // Per-turn cost guard: bound cumulative tool executions regardless of the
      // configured loop depth. Breaking here routes to the forced-finalization
      // safety net below so the turn still resolves to a final payload.
      if (totalToolCalls >= OPENAI_NATIVE_TOOL_CALL_BUDGET) {
        recordOpenAINativeMetric("tool_loop_budget_exhausted", {
          totalToolCalls,
          budget: OPENAI_NATIVE_TOOL_CALL_BUDGET,
          effectiveLoopLimit,
        });
        break;
      }
    }

    let replayState = collectReplayFunctionCallState(conversationHistory);
    if (replayState.missingCallIdCount > 0) {
      recordOpenAINativeMetric("tool_loop_failure", {
        reason: "missing_call_id",
        stage: "boundary",
        missingCallIdCount: replayState.missingCallIdCount,
      });
      callbacks.onError(FUNCTION_CALL_MISSING_ID_ERROR);
      return;
    }

    let remainingCalls = replayState.unresolvedCalls;
    if (remainingCalls.length > 0) {
      recordOpenAINativeMetric("tool_loop_boundary_finalize_attempt", {
        pendingCalls: remainingCalls.length,
        configuredLoopLimit: effectiveLoopLimit,
        effectiveLoopLimit,
      });

      // Every replayed function_call must have a corresponding function_call_output.
      const boundaryFallback = buildFallbackOutputsForUnansweredCalls(
        remainingCalls,
        FUNCTION_CALL_NO_OUTPUT_ERROR
      );
      callbacks.onToolResult?.(boundaryFallback.fallbackResults);

      const forcedFinalizationReplay = [...conversationHistory, ...boundaryFallback.fallbackOutputs];
      const forcedFinalizationInput = buildFollowUpInput(forcedFinalizationReplay, referenceImages);
      const forcedFinalizationResponse = await sendOpenAIRequest(
        createNativeForcedFinalizationRequestBody({
          model,
          systemPrompt,
          tools,
          input: forcedFinalizationInput,
          toolPolicy,
        }),
        "OPENAI-NATIVE-FINALIZE",
        2,
        options,
        makeHopObserver(lastHopIndex + 1),
        "multi_hop_executor"
      );
      responseData = forcedFinalizationResponse;
      conversationHistory = appendResponseOutputToConversationHistory(
        forcedFinalizationReplay,
        forcedFinalizationResponse
      );
      replayState = collectReplayFunctionCallState(conversationHistory);
      if (replayState.missingCallIdCount > 0) {
        recordOpenAINativeMetric("tool_loop_failure", {
          reason: "missing_call_id",
          stage: "post_finalize",
          missingCallIdCount: replayState.missingCallIdCount,
        });
        callbacks.onError(FUNCTION_CALL_MISSING_ID_ERROR);
        return;
      }
      remainingCalls = replayState.unresolvedCalls;
    }

    if (!terminatedByNoFunctionCalls && remainingCalls.length === 0) {
      maybeEmitThinking(responseData, callbacks);
    }
    if (remainingCalls.length > 0) {
      recordOpenAINativeMetric("tool_loop_failure", {
        reason: "loop_limit_exceeded",
        pendingCalls: remainingCalls.length,
      });
      callbacks.onError("OpenAI tool loop exceeded max iterations before reaching final output.");
      return;
    }

    const structuredPayload = extractStructuredPayload(responseData);
    let parsedPayload: Record<string, unknown> | null = structuredPayload;

    if (!parsedPayload) {
      const fallbackText = extractMessageTextContent(responseData);
      if (fallbackText) {
        try {
          parsedPayload = parseJsonText(fallbackText);
        } catch (parseError) {
          const repaired = tryRepairTruncatedJson(fallbackText);
          if (repaired) {
            parsedPayload = parseJsonText(repaired);
          } else {
            recordOpenAINativeMetric("parse_failure", {
              source: "native_message_text",
              message:
                parseError instanceof Error ? parseError.message : String(parseError),
            });
          }
        }
      }
    }

    if (!parsedPayload) {
      const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
      recordOpenAINativeMetric("parse_failure", {
        source: "native_structured_output",
        elapsedSeconds: Number.parseFloat(elapsed),
      });
      callbacks.onError("OpenAI native mode returned no parseable structured payload.");
      return;
    }

    const finalized = finalizeModelModifications(parsedPayload as unknown as PresentationModification);
    recordOpenAINativeMetric("apply_diagnostics", {
      diagnostics: finalized.diagnostics?.length || 0,
    });
    callbacks.onComplete(finalized);
  } catch (error) {
    const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
    const errMsg = error instanceof Error ? error.message : "Unknown error";
    recordOpenAINativeMetric("request_failure", {
      elapsedSeconds: Number.parseFloat(elapsed),
      message: errMsg,
    });
    callbacks.onError(
      `Request failed after ${elapsed}s: ${errMsg}${errMsg === "Load failed" ? " (WebKit timeout — the AI took too long to respond)" : ""}`
    );
  }
}

export async function queryOpenAI(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const telemetry = getOpenAINativeRolloutTelemetry();
  console.log(
    `[OPENAI_NATIVE] rollout enabled=${telemetry.enabled} stage=${telemetry.stage}${telemetry.bucket !== undefined ? ` bucket=${telemetry.bucket}` : ""} source=${telemetry.source}`
  );

  if (!isOpenAINativeEnabled()) {
    recordOpenAINativeMetric("legacy_fallback_invoked", { reason: "rollout_disabled" });
    return queryOpenAILegacy(
      presentationContent,
      userQuery,
      callbacks,
      model,
      slideImages,
      chatHistory,
      toolPolicy,
      options
    );
  }

  return queryOpenAINative(
    presentationContent,
    userQuery,
    callbacks,
    model,
    slideImages,
    chatHistory,
    toolPolicy,
    options
  );
}
